import { createPortal } from 'react-dom';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import DrawerHeader from './components/DrawerHeader';
import SectionBlock from './components/SectionBlock';
import DegradedBanner from './components/DegradedBanner';
import { UnitsProvider } from './UnitsContext';
import { ReconExpandProvider } from './ReconExpandContext';
import { NO_RECONS_IN_SCOPE, RECONCILIATIONS_LOADING, RECONCILIATIONS_UNAVAILABLE } from './constants/reconciliationsCopy';
import { useReconciliationsLogic } from './hooks/useReconciliationsLogic';
import { selectReconLabelsById } from '../../../../store/reconciliations/selectors';
import styles from './ReconciliationsDrawer.module.scss';

interface ReconciliationsDrawerProps {
    orgId: string;
    onClose: () => void;
}

/** Desktop only — no mobile entry point exists for Reconciliations. */
function ReconciliationsDrawer({ orgId, onClose }: Readonly<ReconciliationsDrawerProps>) {
    const { entry, selectedFys, setSelectedFys } = useReconciliationsLogic(orgId);
    const sections = entry?.payload?.sections ?? [];
    const degraded = entry?.payload?.degraded;

    // The only empty state: a payload with no sections. A year with no data is just a row of N/A cells,
    // never a message.
    const emptyState = !sections.length ? NO_RECONS_IN_SCOPE : null;

    const showSections = entry?.status === 'success' && !emptyState;

    // One region, always mounted, whose TEXT changes — assistive tech announces an update to a
    // live region's content, and says nothing when one element is swapped out for another.
    const isLoading = !entry || entry.status === 'loading';
    const stateMessage = isLoading ? RECONCILIATIONS_LOADING : entry.status === 'error' ? RECONCILIATIONS_UNAVAILABLE : emptyState ?? '';

    // The title sits in this drawer's own header row rather than SideDrawer's `showHeading` +
    // `headerActions` pair, which renders two boxes with the second absolutely positioned over the
    // first — that is what put the title and the controls on different lines. `heading` is still
    // passed: it is the dialog's aria-label. SideDrawer keeps rendering the close button, and the
    // header row reserves the space it is absolutely positioned into.
    //
    // Portalled to document.body: the tab pills mount this drawer inside a sticky, z-indexed
    // header row (Financials/Banking `.Selector`, GST `.GstPage` isolation), and a fixed backdrop
    // cannot escape its ancestor's stacking context — the dashboard header then paints over it.
    return createPortal(
        <SideDrawer allowClose onClose={onClose} width="77.375rem" heading="Reconciliations">
            <UnitsProvider>
                <ReconExpandProvider>
                    <div className={styles.Drawer}>
                        <DrawerHeader
                            selectedFys={selectedFys}
                            onFysChange={setSelectedFys}
                            orgId={orgId}
                            showExpandAll={showSections}
                        />
                        <div className={styles.Body}>
                            <div className={styles.Content}>
                                {isLoading && (
                                    <div className={styles.LoadingState} aria-hidden="true">
                                        <LoadingSpinner height="2.5rem" color="var(--primary-text-colour)" style={{ margin: 0 }} />
                                    </div>
                                )}
                                <div role="status" aria-live="polite" className={stateMessage && !isLoading ? styles.State : styles.SrOnly}>
                                    {stateMessage}
                                </div>
                                {entry?.status === 'success' && degraded?.isDegraded && (
                                    <DegradedBanner degraded={degraded} reconLabelById={selectReconLabelsById(entry)} />
                                )}
                                {showSections &&
                                    sections.map((section) => (
                                        <SectionBlock
                                            key={section.id}
                                            orgId={orgId}
                                            fys={selectedFys.length ? selectedFys : null}
                                            section={section}
                                        />
                                    ))}
                            </div>
                        </div>
                    </div>
                </ReconExpandProvider>
            </UnitsProvider>
        </SideDrawer>,
        document.body,
    );
}

export default ReconciliationsDrawer;
